// @ts-strict-ignore
import * as React from 'react';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import LogoWordmark from '../../../assets/runway-logo-horizontal.svg';
import { useIsMobile } from '@/mediaQueriesHelpers';
import { useHistory, useRouteMatch } from 'react-router-dom';
import routes from '@/routes';
import Text from 'antd/lib/typography/Text';
import { Avatar, Button, Dropdown, Layout, List, MenuProps, Popover, Space, Spin, Tabs, Tooltip } from 'antd/lib';
import { useGetExternalBuildDistroBucket } from '../hooks/useGetExternalBuildDistroBucket';
import { useGetExternalBuildDistroBucketBuilds } from '../hooks/useGetExternalBuildDistroBucketBuilds';
import {
    BuildArtifact,
    BuildDistroArtifactDownloadStatus,
    BuildDistroBuildStatus,
    ExternalBuildDistroBucket,
    ExternalBuildDistroBuild,
} from '@/types';
import { globalMessage } from '@/utils/globalMessage';
import { Content } from 'antd/lib/layout/layout';
import {
    ArtifactTag,
    BucketAccessIconTooltip,
    BuildBucketAvatar,
    BuildNumberTagBase,
    BuildTypeAndDate,
    ListDataSourceType,
} from '../buildDistribution/CommonComponents';
import {
    artifactTypeDescription,
    bucketDescriptionForType,
    displayTextForAppPlatform,
    helperTextForArtifactType,
} from '@/helpers/displayHelpers';
import {
    CloudDownloadOutlined,
    DownloadOutlined,
    DownOutlined,
    InfoCircleOutlined,
    LinkOutlined,
    QrcodeOutlined,
    ShareAltOutlined,
    SolutionOutlined,
    TabletOutlined,
} from '@ant-design/icons';
import { BucketDetailsTabTitle } from '../buildDistribution/BuildBucketDetails';
import MarkdownComponent from '../HOCs/Markdown';
import {
    canPlatformInstallBinaries,
    getShouldDisableInstallationForBinaryType,
    isGooglePlatform,
    shareLink,
} from '@/helpers';
import {
    DrawerList,
    DrawerListItem,
    DrawerSection,
    DrawerSectionsWrapper,
    ResponsiveDrawer,
} from '../buildDistribution/mobileComponents/ResponsiveDrawer';
import { useGetExternalBuildDistroBuild } from '../hooks/useGetExternalBuildDistroBuild';
import { RunwayErrorType } from '@/api';
import { createExternalBuildDistroBucketBuildRoute, createExternalBuildDistroBuildRoute } from '@/helpers/routeHelpers';
import { MenuItemType } from 'antd/lib/menu/interface';
import { QRCode } from 'react-qrcode-logo';
import { PLATFORM_AVAILABILITY_MESSAGE } from '@/constants';
import ArtifactsModule from '../workflowModule/ArtifactsModule';

interface PublicBuildDistroComponentMatchParams {
    bucketHashParam: string;
    buildHashParam: string;
}

export const PublicBuildDistroContainer: React.FC = () => {
    var match = useRouteMatch<PublicBuildDistroComponentMatchParams>([routes.publicBuild]);
    if (!match?.params) {
        match = useRouteMatch<PublicBuildDistroComponentMatchParams>([routes.publicBucket]);
    }
    const bucketHash = match?.params?.bucketHashParam;
    const buildHash = match?.params?.buildHashParam;

    return <PublicBuildDistroBucketDetails bucketHash={bucketHash} buildHash={buildHash} />;
};

interface PublicBuildDistroComponentProps {
    bucketHash: string;
    buildHash?: string;
}

enum PublicBuildBucketDetailsTab {
    Builds = 'builds',
}

export const PublicBuildDistroBucketDetails: React.FC<PublicBuildDistroComponentProps> = ({
    bucketHash,
    buildHash,
}) => {
    const history = useHistory();
    const isMobile = useIsMobile();

    const searchParams = new URLSearchParams(window.location.search);
    const selectedBuildHash = searchParams.get('build');

    const [bucket, setBucket] = React.useState<ExternalBuildDistroBucket>();
    const [getExternalBucket, isLoadingExternalBucket, getExternalBucketError] = useGetExternalBuildDistroBucket();

    const [bucketBuilds, setBucketBuilds] = React.useState<ExternalBuildDistroBuild[]>([]);
    const [getExternalBucketBuilds, areExternalBucketBuildsLoading, getExternalBucketBuildsError] =
        useGetExternalBuildDistroBucketBuilds();

    const [selectedBuild, setSelectedBuild] = React.useState<ExternalBuildDistroBuild>(null);
    const [getExternalBuild, isExternalBuildLoading, getExternalBuildError] = useGetExternalBuildDistroBuild();

    const [latestError, setLatestError] = React.useState(null);

    const isPublicBuild = !!buildHash;

    React.useEffect(() => {
        setLatestError(getExternalBucketError);
    }, [getExternalBucketError]);

    React.useEffect(() => {
        setLatestError(getExternalBucketBuildsError);
    }, [getExternalBucketBuildsError]);

    React.useEffect(() => {
        setLatestError(getExternalBuildError);
    }, [getExternalBuildError]);

    React.useEffect(() => {
        if (latestError) {
            if (latestError.name && latestError.name === RunwayErrorType.Unauthorized) {
                globalMessage.error('This resource is not currently public.');
                history.replace(routes.login);
            } else {
                globalMessage.error('Error retrieving requested bucket resource.');
            }
        }
    }, [latestError]);

    const bucketDesc = bucket ? bucketDescriptionForType(bucket.type) : null;

    const [activeTab, setActiveTab] = React.useState(PublicBuildBucketDetailsTab.Builds);

    const requestBucketAndBuilds = async () => {
        const buildHashToUse = selectedBuildHash ?? buildHash;
        const bucketResult = await getExternalBucket(bucketHash, buildHashToUse);
        if (bucketResult) {
            setBucket(bucketResult);

            if (bucketResult.publicBucketLinkEnabled) {
                const buildsResult = await getExternalBucketBuilds(bucketHash);
                if (buildsResult) {
                    setBucketBuilds(buildsResult);
                }
            } else if (buildHashToUse) {
                const buildResult = await getExternalBuild(buildHashToUse, bucketHash);
                if (buildResult) {
                    setBucketBuilds([buildResult]);
                }
            }
        }
    };

    const requestSingleBuild = async (buildHash: string, bucketHash: string) => {
        const result = await getExternalBuild(buildHash, bucketHash);
        if (result) {
            setSelectedBuild(result);
        }
    };

    React.useEffect(() => {
        requestBucketAndBuilds();
    }, [bucketHash]);

    React.useEffect(() => {
        if (selectedBuildHash) {
            const buildSelection = bucketBuilds.find((build) => build.id === selectedBuildHash);

            if (buildSelection) {
                setSelectedBuild(buildSelection);
            } else {
                requestSingleBuild(selectedBuildHash, bucketHash);
            }
        } else {
            setSelectedBuild(null);
        }
    }, [selectedBuildHash]);

    return (
        <PublicAppHeader
            title={bucket?.name ?? ''}
            isLoading={isLoadingExternalBucket || areExternalBucketBuildsLoading}
            titleFontSize={isMobile ? 14 : 20}
            headerComponent={
                <div
                    style={{
                        backgroundColor: antGrey.grey10,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100vw',
                        position: 'fixed',
                        height: isMobile ? '67px' : '80px',
                        top: '0px',
                        left: '0px',
                        zIndex: 10,
                        gap: '20px',
                        padding: '20px 20px 20px',
                        boxShadow: 'var(--rw-shadow-lg)',
                    }}
                >
                    {bucket && (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    flexShrink: 1,
                                    alignItems: 'center',
                                    minWidth: '0px',
                                    gap: '16px',
                                }}
                            >
                                <div style={{ flexShrink: 0 }}>
                                    <Avatar shape="square" size={isMobile ? 27 : 40} src={bucket.appImageUrl} />
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexShrink: 2,
                                        minWidth: 0,
                                        gap: '8px',
                                    }}
                                >
                                    <Text
                                        ellipsis
                                        style={{
                                            flexShrink: 2,
                                            fontSize: isMobile ? 16 : 20,
                                            color: antGrey.grey2,
                                            minWidth: '0px',
                                        }}
                                    >
                                        {bucket.appName}
                                    </Text>
                                    {!bucket.shouldOmitPlatformFromAppName && (
                                        <Text
                                            ellipsis
                                            style={{
                                                flexShrink: 2,
                                                fontSize: isMobile ? 16 : 20,
                                                color: antGrey.grey7,
                                                wordBreak: 'normal',
                                                minWidth: '35px',
                                            }}
                                        >
                                            {displayTextForAppPlatform(bucket.platform)}
                                        </Text>
                                    )}
                                </div>
                            </div>
                            <img
                                src={LogoWordmark}
                                alt="Runway"
                                style={{
                                    flexShrink: 0,
                                }}
                                height={isMobile ? 16 : 20}
                            />
                        </>
                    )}
                </div>
            }
            topLeftComponent={(titleComponent) => (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'start',
                        gap: isMobile ? 16 : 28,
                        marginTop: 8,
                    }}
                >
                    {bucket && (
                        <BuildBucketAvatar appImageUrl={bucket.appImageUrl} size={isMobile ? 25 : 54} bucket={bucket} />
                    )}
                    <Space
                        direction="vertical"
                        size={4}
                        style={{
                            marginTop: isMobile ? 0 : 4,
                        }}
                    >
                        <Space size={12}>
                            {titleComponent}
                            {bucket && !isPublicBuild && <BucketAccessIconTooltip isExternalBucketView={true} />}
                        </Space>
                        {bucketDesc && <Text type="secondary">{bucketDesc}</Text>}
                    </Space>
                </div>
            )}
        >
            {bucket && bucketBuilds && (
                <Tabs
                    type="card"
                    destroyInactiveTabPane
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key as PublicBuildBucketDetailsTab)}
                    items={[
                        {
                            key: PublicBuildBucketDetailsTab.Builds,
                            label: (
                                <BucketDetailsTabTitle
                                    title={`Builds (${isPublicBuild ? bucketBuilds?.length : (bucket?.totalBuildsCount ?? 0)})`}
                                    isActive={activeTab === PublicBuildBucketDetailsTab.Builds}
                                    icon={TabletOutlined}
                                />
                            ),
                            children: (
                                <>
                                    <Space
                                        direction="vertical"
                                        style={{
                                            width: '100%',
                                        }}
                                        size="large"
                                    >
                                        <PublicBucketBuildsList
                                            externalBuilds={bucketBuilds}
                                            destinationURLFactory={
                                                isPublicBuild ?
                                                    createExternalBuildDistroBuildRoute
                                                :   createExternalBuildDistroBucketBuildRoute
                                            }
                                            isMobile={isMobile}
                                            isPublicBuild={isPublicBuild}
                                        />
                                    </Space>
                                    <PublicBuildDetailsDrawer
                                        bucket={bucket}
                                        build={selectedBuild}
                                        isLoading={isExternalBuildLoading}
                                        isMobile={isMobile}
                                        onCancel={() => {
                                            setSelectedBuild(null);
                                            const url = new URL(location.href);
                                            const searchParams = new URLSearchParams(location.search);
                                            searchParams.delete('build');
                                            url.search = searchParams.toString();
                                            window.history.pushState({}, '', url);
                                        }}
                                    />
                                </>
                            ),
                        },
                    ]}
                ></Tabs>
            )}
        </PublicAppHeader>
    );
};

interface PublicAppHeaderProps {
    title: string;
    children: React.ReactNode;
    topRightComponent?: React.ReactNode;
    topLeftComponent?: React.ReactNode | ((titleComponent: React.ReactNode) => React.ReactNode);
    isLoading?: boolean;
    titleFontSize?: number;
    headerComponent?: React.ReactNode;
}

const PublicAppHeader: React.FC<PublicAppHeaderProps> = ({
    title,
    children,
    topRightComponent,
    topLeftComponent,
    isLoading,
    titleFontSize,
    headerComponent,
}) => {
    const isMobile = useIsMobile();
    const titleComponent = (
        <Text ellipsis style={{ ...titleStyle, fontSize: titleFontSize ?? 16 }} type="secondary">
            {title}
        </Text>
    );

    return (
        <Layout
            style={{
                marginBottom: isMobile ? 0 : 24,
            }}
        >
            <Content
                style={{
                    display: 'flex',
                    gap: 12,
                    flexDirection: 'column',
                }}
            >
                {isLoading ?
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            flexGrow: 1,
                        }}
                    >
                        <Spin />
                    </div>
                :   <>
                        {headerComponent}
                        <div
                            style={{
                                padding: isMobile ? '20px 16px' : '30px 36px',
                                marginBottom: '50px',
                                position: 'relative',
                                top: isMobile ? '67px' : '80px',
                                left: '0px',
                                overflow: 'scroll',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    alignItems: 'start',
                                    marginBottom: 16,
                                }}
                            >
                                {typeof topLeftComponent === 'function' ?
                                    topLeftComponent(titleComponent)
                                :   <Space>
                                        {titleComponent}
                                        {topLeftComponent}
                                    </Space>
                                }
                                {topRightComponent}
                            </div>
                            {children}
                        </div>
                    </>
                }
            </Content>
        </Layout>
    );
};

interface PublicBucketBuildsListParams {
    externalBuilds: ExternalBuildDistroBuild[];
    isMobile?: boolean;
    isLoading?: boolean;
    shouldShowBucketType?: boolean;
    destinationURLFactory: (buildId: string, bucketId?: string) => string;
    showSharedInfo?: boolean;
    onLoadMore?: () => void;
    showLoadMore?: boolean;
    leftInset?: boolean;
    isPublicBuild: boolean;
}

const PublicBucketBuildsList: React.FC<PublicBucketBuildsListParams> = ({
    externalBuilds,
    isMobile,
    isLoading,
    shouldShowBucketType,
    destinationURLFactory,
    onLoadMore,
    showLoadMore,
    leftInset,
    isPublicBuild,
}) => {
    const history = useHistory();

    return (
        <List
            loadMore={
                onLoadMore &&
                showLoadMore && (
                    <div
                        style={{
                            textAlign: 'center',
                            marginTop: 12,
                            height: 32,
                            lineHeight: '32px',
                        }}
                    >
                        <Button type="link" onClick={onLoadMore}>
                            Load more...
                        </Button>
                    </div>
                )
            }
            style={{
                marginLeft: isMobile && shouldShowBucketType ? 12 : 0,
                marginRight: isMobile && shouldShowBucketType ? 12 : 0,
            }}
            locale={{
                emptyText: 'No builds yet.',
            }}
            loading={isLoading}
            dataSource={externalBuilds}
            split
            renderItem={(build) => {
                if (!!build.binaryBuild || !!build.ciBuild) {
                    return (
                        <List.Item
                            className="build-row"
                            onClick={() => {
                                const url = destinationURLFactory(build.id, build.bucketId);
                                history.push(url);
                            }}
                            style={{ paddingLeft: leftInset && !isMobile ? 34 : 4, paddingRight: 4 }}
                        >
                            <PublicBuildRow build={build} isMobile={isMobile} isPublicBuild={isPublicBuild} />
                        </List.Item>
                    );
                } else {
                    return null;
                }
            }}
        />
    );
};

interface PublicBuildRowProps {
    build: ExternalBuildDistroBuild;
    isMobile?: boolean;
    isPublicBuild?: boolean;
}

const PublicBuildRow: React.FC<PublicBuildRowProps> = ({ build, isMobile, isPublicBuild }) => {
    if (!build) {
        return null;
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Space
                size={24}
                style={{
                    cursor: 'pointer',
                    flex: 1,
                }}
                align="center"
                wrap
            >
                <Space style={{ width: 130 }} size={16} align="center">
                    <BuildTypeAndDate uploadedAt={build.uploadedAt} ciBuild={build.ciBuild} />
                </Space>
                {(!!build.binaryBuild || !!build.ciBuild) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <PublicBuildNumberTag build={build} fontSize={15} showBadge />
                        {build.binaryBuild && !isMobile && (
                            <ArtifactTag artifactFileName={build.binaryBuild.artifactFileName}></ArtifactTag>
                        )}
                    </div>
                )}
            </Space>
            <Space
                align="center"
                onClick={(e) => {
                    e.stopPropagation();
                }}
                size={4}
            >
                <BucketAccessIconTooltip isExternalBuildView={isPublicBuild} />
                {!isMobile && (
                    <Popover
                        showArrow={false}
                        placement="right"
                        content={
                            <div
                                style={{
                                    width: 210,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 8,
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 12,
                                        color: antGrey.grey7,
                                        textTransform: 'uppercase',
                                        fontWeight: 600,
                                        letterSpacing: '0.1em',
                                        paddingBottom: 8,
                                    }}
                                >
                                    Tester notes
                                </Text>
                                {build.testerNotes ?
                                    <MarkdownComponent
                                        source={build.testerNotes}
                                        style={{
                                            width: 310,
                                            fontSize: 12,
                                        }}
                                        className="markdown-preview-with-breaks"
                                    />
                                :   <Text
                                        style={{
                                            fontSize: 12,
                                            color: antGrey.grey6,
                                            fontWeight: 400,
                                        }}
                                    >
                                        No notes
                                    </Text>
                                }
                            </div>
                        }
                    >
                        <Button icon={<SolutionOutlined />} type="link" />
                    </Popover>
                )}
            </Space>
        </div>
    );
};

const PublicBuildNumberTag: React.FC<{
    build: ExternalBuildDistroBuild;
    backgroundColor?: string;
    fontSize?: number;
    showBadge?: boolean;
}> = ({ build, backgroundColor, fontSize, showBadge }) => {
    if (!build) {
        return null;
    }

    return (
        <BuildNumberTagBase
            ciBuildIdentifier={build.ciBuild?.buildIdentifier}
            ciIntegrationId={build.ciBuild?.integrationId}
            ciBuildStatus={build.ciBuild && showBadge ? build.ciBuild.buildStatus : null}
            binaryBuildIdentifier={build.binaryBuild?.buildNumber}
            binaryBuildVersion={build.binaryBuild?.buildVersion}
            backgroundColor={backgroundColor}
            fontSize={fontSize}
        />
    );
};

interface PublicBuildDetailsDrawerProps {
    build?: ExternalBuildDistroBuild;
    bucket: ExternalBuildDistroBucket;
    isLoading: boolean;
    isMobile?: boolean;
    onCancel: () => void;
    afterOpenChange?: (open: boolean) => void;
}

const PublicBuildDetailsDrawer: React.FC<PublicBuildDetailsDrawerProps> = ({ build, bucket, isMobile, onCancel }) => {
    const drawerItems: DrawerListItem[] = React.useMemo(() => {
        if (build) {
            const items: ListDataSourceType[] = [];

            if (build.binaryBuild) {
                items.push({
                    title: isGooglePlatform(bucket.platform) ? 'Version code' : 'Build number',
                    description: <Text ellipsis>#{build.binaryBuild.buildNumber}</Text>,
                });

                items.push({
                    title: isGooglePlatform(bucket.platform) ? 'Version name' : 'Version string',
                    description: <Text ellipsis>{build.binaryBuild.buildVersion}</Text>,
                });

                items.push({
                    title: 'Build type',
                    description: <Text>{artifactTypeDescription(build.binaryBuild.buildType) ?? 'Unknown'}</Text>,
                });

                let artifactDesc;
                if (build.binaryBuild.artifactFileName !== '') {
                    if (build.downloadUrl) {
                        artifactDesc = (
                            <Button
                                size="small"
                                type="link"
                                onClick={() => window.open(`${window.location.origin}${build.downloadUrl}`, '_self')}
                            >
                                {build.binaryBuild.artifactFileName}
                            </Button>
                        );
                    } else {
                        artifactDesc = (
                            <Text code style={{ color: runwayColors.darkTextGrey }} ellipsis>
                                {build.binaryBuild.artifactFileName}
                            </Text>
                        );
                    }
                } else {
                    if (build.artifactDownloadStatus === BuildDistroArtifactDownloadStatus.InProgress) {
                        artifactDesc = (
                            <Text style={{ color: runwayColors.statusYellow }}>Trying to resolve artifacts...</Text>
                        );
                    } else {
                        artifactDesc = (
                            <Text ellipsis style={{ color: runwayColors.statusRed }}>
                                Artifact not found.
                            </Text>
                        );
                    }
                }

                items.push({
                    title: 'Artifact file name',
                    description: artifactDesc,
                });
            }

            return items;
        } else {
            return [];
        }
    }, [build]);

    const buildType = build?.binaryBuild?.buildType;
    const hasBinaryBuild = !!build?.binaryBuild;

    const shouldDisableInstallationForBinaryType =
        buildType ? getShouldDisableInstallationForBinaryType(buildType) : false;
    const shouldDisableInstallationForBinaryBuild = hasBinaryBuild ? shouldDisableInstallationForBinaryType : true;

    const shouldDisableBasedOnPlatform =
        // canPlatformInstallBinaries(bucket.platform) && !isApplePlatformAndLacksProvisioning
        canPlatformInstallBinaries(bucket.platform) ? shouldDisableInstallationForBinaryBuild : true;

    const isDisabled = build?.status === BuildDistroBuildStatus.Expired ? true : shouldDisableBasedOnPlatform;

    let installationHelperText;
    if (build?.status === BuildDistroBuildStatus.Expired) {
        installationHelperText = 'This build has expired.';
    } else if (canPlatformInstallBinaries(bucket.platform)) {
        if (buildType) {
            installationHelperText = helperTextForArtifactType(buildType);
        } else if (hasBinaryBuild) {
            installationHelperText =
                'We were unable to infer the profile and signing certificate type of this build. It may not be installable on your device.';
        } else {
            installationHelperText = 'No build artifact found.';
        }
    } else {
        installationHelperText = PLATFORM_AVAILABILITY_MESSAGE;
    }

    return (
        <>
            <ResponsiveDrawer
                canCollapseOnMobile
                open={!!build}
                onClose={() => {
                    onCancel();
                }}
                header={
                    bucket &&
                    build && (
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Space size={18}>
                                <BuildBucketAvatar appImageUrl={bucket.appImageUrl} size={32} bucket={bucket} />
                                <PublicBuildNumberTag build={build} backgroundColor={antGrey.grey4} fontSize={16} />
                            </Space>
                            {!bucket.publicBucketLinkEnabled && build.publicBuildLinkEnabled && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                    }}
                                >
                                    <BucketAccessIconTooltip isExternalBuildView />
                                </div>
                            )}
                        </div>
                    )
                }
                content={
                    bucket &&
                    build && (
                        <DrawerSectionsWrapper>
                            {hasBinaryBuild && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        padding: 16,
                                        gap: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'stretch',
                                            gap: 16,
                                        }}
                                    >
                                        {isMobile ?
                                            <div
                                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '50%' }}
                                            >
                                                <Button
                                                    block
                                                    onClick={() => {
                                                        const buildUrl = `${window.location.origin}/bucket/${build.bucketId}?build=${build.id}`;
                                                        shareLink(buildUrl);
                                                    }}
                                                >
                                                    <Space>
                                                        Share
                                                        <ShareAltOutlined />
                                                    </Space>
                                                </Button>
                                            </div>
                                        :   <PublicBuildDistroShareMenu
                                                build={build}
                                                dropDownStyle={{ width: '50%' }}
                                            />
                                        }
                                        {isMobile ?
                                            <PublicBuildDistroInstallButton
                                                build={build}
                                                isDisabled={isDisabled}
                                                installationHelperText={installationHelperText}
                                            />
                                        :   <PublicBuildDistroInstallMenu
                                                build={build}
                                                isDisabled={isDisabled}
                                                installationHelperText={installationHelperText}
                                                dropDownStyle={{ width: '50%' }}
                                                shouldShowInfoIcon={true}
                                            >
                                                <Button
                                                    block
                                                    type="primary"
                                                    disabled={isDisabled}
                                                    icon={<CloudDownloadOutlined />}
                                                >
                                                    <Space>
                                                        Install
                                                        <DownOutlined />
                                                    </Space>
                                                </Button>
                                            </PublicBuildDistroInstallMenu>
                                        }
                                    </div>
                                    {isMobile && (
                                        <Text type="secondary" style={{ textAlign: 'center' }}>
                                            {installationHelperText}
                                        </Text>
                                    )}
                                </div>
                            )}
                            <DrawerSection title="Notes" canCollapseOnMobile>
                                {build?.testerNotes ?
                                    <MarkdownComponent
                                        source={build.testerNotes}
                                        style={{ width: 310 }}
                                        className="markdown-preview-with-breaks"
                                    />
                                :   <Text type="secondary">No tester notes have been added.</Text>}
                            </DrawerSection>
                            {drawerItems.length > 0 && (
                                <DrawerSection title="Build details" canCollapseOnMobile startsCollapsed>
                                    <Space direction="vertical" size={12}>
                                        <DrawerList items={drawerItems} />
                                    </Space>
                                </DrawerSection>
                            )}
                            {!!build.additionalArtifacts && (
                                <DrawerSection title="Additional artifacts" canCollapseOnMobile startsCollapsed>
                                    <Space direction="vertical" size={12}>
                                        <ArtifactsModule
                                            appId={''}
                                            orgId={''}
                                            releaseId={''}
                                            version={''}
                                            artifacts={(build.additionalArtifacts as BuildArtifact[]) ?? []}
                                            services={null}
                                            stabilityIntegrations={null}
                                            appIdentifiers={null}
                                            buildStatus={null}
                                            canRetry={true}
                                            showHeader={false}
                                            isPublic={true}
                                            publicBuildDownloadUrl={build.downloadUrl}
                                            stepRunComponentProps={null}
                                        />
                                    </Space>
                                </DrawerSection>
                            )}
                        </DrawerSectionsWrapper>
                    )
                }
            ></ResponsiveDrawer>
        </>
    );
};

interface PublicBuildDistroInstallMenuProps {
    build: ExternalBuildDistroBuild;
    dropDownStyle?: React.CSSProperties;
    isDisabled: boolean;
    installationHelperText: boolean;
    shouldShowInfoIcon?: boolean;
    children: React.ReactNode;
}

enum PublicInstallOptions {
    Download,
    DisplayQRCode,
}

const PublicBuildDistroInstallMenu: React.FC<PublicBuildDistroInstallMenuProps> = (
    props: PublicBuildDistroInstallMenuProps,
) => {
    const menuItems: MenuItemType[] = [];

    if (props.build.downloadUrl) {
        menuItems.push({
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'space-between',
                        gap: 8,
                        paddingTop: 8,
                    }}
                >
                    <Space align="center">
                        <DownloadOutlined style={{ color: runwayColors.primary }} />
                        <Text>
                            Download{' '}
                            <span style={{ color: runwayColors.primary }}>
                                {props.build.binaryBuild?.artifactFileName ?? props.build.originalFileName}
                            </span>
                        </Text>
                    </Space>
                </div>
            ),
            key: PublicInstallOptions.Download,
        });
    }

    if (props.build.installationUrl) {
        menuItems.push({
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, paddingTop: 8 }}>
                    <Space align="center">
                        <QrcodeOutlined style={{ color: runwayColors.primary }} />
                        <Text>Install from QR Code</Text>
                    </Space>
                    <QRCode value={`${window.location.origin}${props.build.installationUrl}`} size={280} />
                </div>
            ),
            key: PublicInstallOptions.DisplayQRCode,
        });
    }

    const onClickMenuItem = (item) => {
        item.domEvent.stopPropagation();

        switch (parseInt(item.key)) {
            case PublicInstallOptions.Download: {
                window.open(`${window.location.origin}${props.build.downloadUrl}`, '_self');
                break;
            }
            case PublicInstallOptions.DisplayQRCode: {
                break;
            }
        }
    };

    const menuProps: MenuProps = {
        items: menuItems,
        onClick: onClickMenuItem,
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...props.dropDownStyle }}>
                {installationTooltipWrapper(
                    <Dropdown
                        menu={menuProps}
                        trigger={['hover']}
                        disabled={props.isDisabled || menuItems.length == 0}
                        destroyPopupOnHide
                    >
                        {props.children}
                    </Dropdown>,
                    props.isDisabled,
                    props.installationHelperText,
                )}
                {props.shouldShowInfoIcon && !props.isDisabled && (
                    <Tooltip title={props.installationHelperText} placement={'top'}>
                        <InfoCircleOutlined
                            style={{
                                color: runwayColors.info,
                            }}
                        />
                    </Tooltip>
                )}
            </div>
        </>
    );
};

interface PublicBuildDistroInstallButtonProps {
    build: ExternalBuildDistroBuild;
    isDisabled: boolean;
    installationHelperText: string;
}

const PublicBuildDistroInstallButton: React.FC<PublicBuildDistroInstallButtonProps> = (
    props: PublicBuildDistroInstallButtonProps,
) => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '50%' }}>
            {installationTooltipWrapper(
                <Button
                    block
                    type="primary"
                    disabled={props.isDisabled}
                    onClick={() => {
                        window.open(`${window.location.origin}${props.build.installationUrl}`, '_self');
                    }}
                >
                    <Space>
                        Install
                        <CloudDownloadOutlined />
                    </Space>
                </Button>,
                props.isDisabled,
                props.installationHelperText,
            )}
        </div>
    );
};

interface PublicBuildDistroShareMenuProps {
    build: ExternalBuildDistroBuild;
    dropDownStyle?: React.CSSProperties;
}

enum PublicShareOptions {
    CopyURL,
    CopyQRCode,
}

const PublicBuildDistroShareMenu: React.FC<PublicBuildDistroShareMenuProps> = (
    props: PublicBuildDistroShareMenuProps,
) => {
    const qrCodeRef = React.useRef(null);

    const menuItems: MenuItemType[] = [
        {
            label: (
                <div>
                    <Space align="center">
                        <LinkOutlined style={{ color: runwayColors.primary }} />
                        <Text>Copy link to clipboard</Text>
                    </Space>
                </div>
            ),
            key: PublicShareOptions.CopyURL,
            disabled: false,
        },
        {
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, paddingTop: 8 }}>
                    <Space align="center">
                        <QrcodeOutlined style={{ color: runwayColors.primary }} />
                        <Text>Copy QR Code to clipboard</Text>
                    </Space>
                    <QRCode
                        ref={qrCodeRef}
                        value={`${window.location.origin}/bucket/${props.build.bucketId}?build=${props.build.id}`}
                        size={280}
                    />
                </div>
            ),
            key: PublicShareOptions.CopyQRCode,
        },
    ];

    const onClickMenuItem = (item) => {
        item.domEvent.stopPropagation();

        switch (parseInt(item.key)) {
            case PublicShareOptions.CopyQRCode: {
                const canvas = qrCodeRef.current?.canvas?.current;
                if (canvas) {
                    canvas.toBlob((blob) => {
                        navigator.clipboard.write([
                            new ClipboardItem({
                                'image/png': blob,
                            }),
                        ]);
                        globalMessage.success('QR code copied to clipboard!');
                    });
                }
                break;
            }
            case PublicShareOptions.CopyURL: {
                navigator.clipboard.writeText(
                    `${window.location.origin}/bucket/${props.build.bucketId}?build=${props.build.id}`,
                );
                globalMessage.success('Build link copied to clipboard!');
                break;
            }
        }
    };

    const menuProps: MenuProps = {
        items: menuItems,
        onClick: onClickMenuItem,
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...props.dropDownStyle }}>
            <Dropdown menu={menuProps} trigger={['hover']} destroyPopupOnHide>
                <Button block icon={<ShareAltOutlined />}>
                    <Space>
                        Share
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
        </div>
    );
};

const installationTooltipWrapper = (children, isDisabled, helperText) => {
    if (isDisabled) {
        return (
            <Tooltip title={helperText} placement="top">
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            pointerEvents: 'none',
                        }}
                    >
                        {children}
                    </div>
                </div>
            </Tooltip>
        );
    }

    return children;
};
